import { supabase } from '@/lib/supabaseClient';

export type StatusScope = 'organization' | 'project' | 'folder' | 'list';
export type StatusCategory = 'open' | 'active' | 'closed';

export interface TaskStatusDef {
  id: string;
  value: string;
  name: string;
  color?: string | null;
  icon?: string | null;
  category: StatusCategory;
  scope: StatusScope;
  scope_id?: string | null;
  order_index: number;
  created_at: number;
}

function convertFromSupabase(row: Record<string, unknown>): TaskStatusDef {
  return {
    id: row.id as string,
    value: row.value as string,
    name: row.name as string,
    color: row.color as string | null,
    icon: row.icon as string | null,
    category: (row.category as StatusCategory) || 'open',
    scope: row.scope as StatusScope,
    scope_id: row.scope_id as string | null,
    order_index: (row.order_index as number) ?? 0,
    created_at: row.created_at as number,
  };
}

export async function fetchTaskStatuses(scope: StatusScope, scopeId: string): Promise<TaskStatusDef[]> {
  const { data, error } = await supabase
    .from('task_statuses')
    .select('*')
    .eq('scope', scope)
    .eq('scope_id', scopeId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return (data || []).map(convertFromSupabase);
}

export async function saveTaskStatuses(
  statuses: Array<{ value: string; name: string; color?: string | null; icon?: string | null; category?: StatusCategory }>,
  scope: StatusScope,
  scopeId: string
): Promise<void> {
  // Delete existing for this scope
  const { error: delError } = await supabase
    .from('task_statuses')
    .delete()
    .eq('scope', scope)
    .eq('scope_id', scopeId);

  if (delError) throw delError;

  if (statuses.length === 0) return;

  const now = Date.now();
  const rows = statuses.map((s, i) => ({
    value: s.value,
    name: s.name,
    color: s.color ?? null,
    icon: s.icon ?? null,
    category: s.category ?? 'open',
    scope,
    scope_id: scopeId,
    order_index: i,
    created_at: now,
  }));

  const { error: insError } = await supabase.from('task_statuses').insert(rows);
  if (insError) throw insError;
}

export async function deleteTaskStatusesByScope(scope: StatusScope, scopeId: string): Promise<void> {
  const { error } = await supabase
    .from('task_statuses')
    .delete()
    .eq('scope', scope)
    .eq('scope_id', scopeId);

  if (error) throw error;
}
